import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { wsService } from "../../services/websocket";
import { NamePrompt } from "../../components/bingo/NamePrompt";
import {
  getStoredPlayerName,
  setStoredPlayerName,
  getClientId,
  getBrowserFingerprint,
  setStoredPlayerId,
  setCurrentObsSession,
} from "../../utils/storage";
import "./CreateBingoView.css";
import { CenteredTextarea } from "../../components/bingo/CenteredTextarea/CenteredTextarea";

const MIN_GRID_SIZE = 3;
const MAX_GRID_SIZE = 7;
const LAST_BINGO_FIELDS_KEY = "bingo_last_fields";

export const CreateBingoView: React.FC = () => {
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [gridSize, setGridSize] = useState<number>(5);
  const [cellTexts, setCellTexts] = useState<string[]>(Array(25).fill(""));
  const [hasOldFields, setHasOldFields] = useState<boolean>(false);
  const [overwriteAll, setOverwriteAll] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientId = getClientId();

  // Check for stored player name and old bingo fields
  useEffect(() => {
    const storedName = getStoredPlayerName();
    if (storedName) {
      setPlayerName(storedName);
    }
    // Check for old bingo fields
    try {
      const raw = localStorage.getItem(LAST_BINGO_FIELDS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          setHasOldFields(true);
        }
      }
    } catch {}
  }, []);

  // Save current fields to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(LAST_BINGO_FIELDS_KEY, JSON.stringify(cellTexts));
    } catch {}
  }, [cellTexts]);
  // Load old bingo fields from localStorage
  const handleLoadOldFields = () => {
    try {
      const raw = localStorage.getItem(LAST_BINGO_FIELDS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          setCellTexts((prev) => {
            const newArr = [...prev];
            for (let i = 0; i < Math.min(arr.length, totalCells); i++) {
              newArr[i] = arr[i] || "";
            }
            return newArr;
          });
        }
      }
    } catch {}
  };

  const totalCells = gridSize * gridSize;

  const handleGridSizeChange = useCallback((newSize: number) => {
    const clampedSize = Math.max(
      MIN_GRID_SIZE,
      Math.min(MAX_GRID_SIZE, newSize),
    );
    setGridSize(clampedSize);
    const newTotalCells = clampedSize * clampedSize;
    setCellTexts((prev) => {
      if (newTotalCells > prev.length) {
        return [...prev, ...Array(newTotalCells - prev.length).fill("")];
      }
      return prev.slice(0, newTotalCells);
    });
  }, []);

  const handleCellTextChange = useCallback((index: number, text: string) => {
    setCellTexts((prev) => {
      const updated = [...prev];
      updated[index] = text;
      return updated;
    });
  }, []);

  const handleCreateLobby = async () => {
    if (!playerName) {
      setShowNamePrompt(true);
      return;
    }
    const textsToSend = cellTexts.slice(0, totalCells);
    const filledTexts = textsToSend.filter((t) => t.trim());
    if (filledTexts.length < totalCells) {
      setError(`Bitte fülle alle ${totalCells} Felder aus`);
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      await wsService.connect();
      const fingerprint = await getBrowserFingerprint();
      const response = await wsService.createLobby({
        gridSize,
        texts: textsToSend,
        playerName,
        clientId,
        fingerprint,
      });
      setStoredPlayerId(response.lobbyCode, response.playerId);
      // Update current OBS session so OBS widget can auto-switch to new game
      setCurrentObsSession(response.lobbyCode, response.playerId);
      navigate(`/play/${response.lobbyCode}`, {
        state: {
          card: response.card,
          isHost: true,
          playerId: response.playerId,
          initialPlayers: response.players,
          hostId: response.hostId,
        },
      });
    } catch (err) {
      console.error("Failed to create lobby:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Lobby konnte nicht erstellt werden. Bitte versuche es erneut.",
      );
      setIsCreating(false);
    }
  };

  const handleNameSubmit = (name: string) => {
    setPlayerName(name);
    setStoredPlayerName(name);
    setShowNamePrompt(false);
  };

  const handleNameClose = () => {
    setShowNamePrompt(false);
  };

  const handleFillSample = () => {
    const sampleTexts = [
      "Thrifty",
      "Clutch",
      "9-3",
      "6-7",
      "Ace",
      "Comeback",
      "Overtime",
      "13-0",
      "Eco",
      "Force Buy",
      "Full Buy",
      "Anti-Eco",
      "Flawless",
      "Team Ace",
      "1v5",
      "1v4",
      "1v3",
      "Wallbang",
      "Headshot",
      "Knife",
      "Ult Wasted",
      "Spike Defused",
      "Spike Exploded",
      "Flanker Died",
      "Lurker Kill",
      "Entry Frag",
      "Exit Frag",
      "Trade Kill",
      "Whiff",
      "Spray Transfer",
      "Collateral",
      "Blind Kill",
      "Flash Assist",
      "Smoke Kill",
      "One Tap",
      "No Scope",
      "Jump Shot",
      "Running Headshot",
      "AFK",
      "Disconnect",
      "Rage Quit",
      "Toxic Chat",
      "GG",
      "Instalock",
      "No Smokes",
      "Dash Play",
      "Dismiss",
      "Revive",
      "Dart Kill",
      "Ult Used",
      "TP Fail",
      "Suppressed",
      "TP",
      "Cam Spot",
      "Lockdown",
      "Plant",
      "Flash",
    ];
    const shuffled = [...sampleTexts].sort(() => Math.random() - 0.5);
    if (overwriteAll) {
      // Overwrite all cells
      setCellTexts(shuffled.slice(0, totalCells));
      return;
    }
    // Fill only empty cells
    setCellTexts((prev) => {
      const updated = [...prev];
      let pickIndex = 0;
      for (let i = 0; i < totalCells; i++) {
        if (!updated[i] || !updated[i].trim()) {
          if (pickIndex >= shuffled.length) break;
          updated[i] = shuffled[pickIndex++] || "";
        }
      }
      return updated.slice(0, totalCells);
    });
  };

  return (
    <div className="create-bingo-view">
      {showNamePrompt && (
        <NamePrompt onSubmit={handleNameSubmit} onClose={handleNameClose} />
      )}

      <div className="create-bingo-view__header">
        <h1>BINGO</h1>
        <div className="create-bingo-view__subtitle">
          Erstelle deine eigene Bingo-Karte
        </div>
      </div>

      <div className="create-bingo-view__config">
        <div className="create-bingo-view__size-control">
          <span>
            {gridSize}×{gridSize}
          </span>
          <input
            type="range"
            min={MIN_GRID_SIZE}
            max={MAX_GRID_SIZE}
            value={gridSize}
            onChange={(e) => handleGridSizeChange(parseInt(e.target.value, 10))}
            className="create-bingo-view__slider"
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={handleFillSample}
            className="create-bingo-view__sample-btn"
          >
            VALORANT
          </button>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--text-muted)",
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={overwriteAll}
              onChange={(e) => setOverwriteAll(e.target.checked)}
            />
            Überschreiben
          </label>
        </div>
      </div>

      <div className="bingo-card">
        <div
          className="bingo-card__grid"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
          }}
        >
          {Array.from({ length: totalCells }).map((_, index) => (
            <div key={index} className="bingo-card__cell">
              <CenteredTextarea
                value={cellTexts[index] || ""}
                onChange={(v) => handleCellTextChange(index, v)}
                placeholder=""
                className="bingo-card__input"
              />
            </div>
          ))}
        </div>
      </div>

      {error && <div className="create-bingo-view__error">{error}</div>}

      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <button
          type="button"
          onClick={handleCreateLobby}
          disabled={isCreating}
          className="create-bingo-view__create-btn"
        >
          {isCreating ? "Erstelle..." : "Lobby erstellen"}
        </button>
      </div>
    </div>
  );
};
